import { BitReader, BitWriter } from "../bitstream.ts";
import { MAX_HEALTH_PACKS } from "../map/mapdata.ts";
import type { Vec3 } from "../math.ts";
import type { SimEvent } from "../sim/events.ts";
import { type InputCmd, readInput, writeInput } from "./input.ts";

/**
 * Wire schema for replicated entities. Every field is quantized to an integer, so delta
 * compression compares integers (no float noise) and decode(encode(x)) is stable.
 */
type Codec =
  | { k: "u"; bits: number }
  | { k: "q"; min: number; max: number; bits: number }
  | { k: "a"; bits: number }
  | { k: "f" };

const u = (bits: number): Codec => ({ k: "u", bits });
const q = (min: number, max: number, bits: number): Codec => ({ k: "q", min, max, bits });
const ang = (bits: number): Codec => ({ k: "a", bits });

export enum EntityType {
  Player = 0,
  Projectile = 1,
  Zone = 2,
  Match = 3,
  Construct = 4,
}

/** Map extent the protocol carries: ±256 m across (maps are Rivals-sized), ~0.5 mm steps. */
const POS_XZ = q(-256, 256, 20);
const POS_Y = q(-50, 50, 17);
const VEL = q(-60, 60, 14);

export const SCHEMAS = {
  [EntityType.Player]: {
    hero: u(6),
    form: u(2),
    baseHero: u(6),
    disguised: u(1),
    team: u(2),
    alive: u(1),
    x: POS_XZ,
    y: POS_Y,
    z: POS_XZ,
    yaw: ang(12),
    pitch: q(-1.6, 1.6, 10),
    hp: u(12),
    armor: u(10),
    shield: u(10),
    over: u(10),
    status: u(20),
    grounded: u(1),
    crouch: u(1),
    action: u(3),
    shots: u(8),
    moveKind: u(5),
    ult: u(7),
    ultActive: u(1),
    respawn: u(5),
    v0: u(16),
    v1: u(16),
    v2: u(16),
    v3: u(16),
  },
  [EntityType.Projectile]: {
    kind: u(9),
    owner: u(16),
    seq: u(16),
    x: POS_XZ,
    y: POS_Y,
    z: POS_XZ,
    vx: VEL,
    vy: VEL,
    vz: VEL,
  },
  [EntityType.Zone]: {
    kind: u(8),
    team: u(2),
    x: POS_XZ,
    y: POS_Y,
    z: POS_XZ,
    radius: q(0, 64, 10),
    ticks: u(12),
  },
  [EntityType.Match]: {
    phase: u(3),
    phaseTicks: u(16),
    round: u(3),
    scoreA: u(2),
    scoreB: u(2),
    progA: q(0, 100, 10),
    progB: q(0, 100, 10),
    owner: u(2),
    capture: q(0, 1, 10),
    capTeam: u(2),
    contested: u(1),
    overtime: u(1),
    onA: u(4),
    onB: u(4),
    /** Bit i: health pack i is up. */
    packs: u(MAX_HEALTH_PACKS),
    winner: u(2),
    mode: u(2),
    /** Control: the stage (sub-map) in play. */
    stage: u(2),
    attacker: u(2),
    /** Payload distance along the track, mm (exact: client prediction collides with it). */
    cart: u(19),
    checkpoint: u(2),
    clock: u(16),
    cartState: u(3),
    goalDone: u(1),
    goalTicks: u(16),
  },
  [EntityType.Construct]: {
    kind: u(8),
    owner: u(16),
    team: u(2),
    x: POS_XZ,
    y: POS_Y,
    z: POS_XZ,
    yaw: ang(10),
    hp: u(13),
    ticks: u(12),
    age: u(12),
    v0: u(16),
    flags: u(8),
  },
} as const satisfies Record<EntityType, Record<string, Codec>>;

export type EntityFields<T extends EntityType> = { [K in keyof (typeof SCHEMAS)[T]]: number };
export type AnyFields = Record<string, number>;

const FIELD_LISTS = Object.fromEntries(
  Object.entries(SCHEMAS).map(([t, s]) => [t, Object.entries(s) as [string, Codec][]]),
) as unknown as Record<EntityType, [string, Codec][]>;

export const STATUS_BITS = {
  stun: 1,
  root: 2,
  slow: 4,
  sleep: 8,
  invuln: 16,
  knockdown: 32,
  boost: 64,
  phased: 128,
  pinned: 256,
  antiHeal: 512,
  healBoost: 1024,
  resist: 2048,
  linked: 4096,
  stealth: 8192,
  haste: 16384,
  unstoppable: 32768,
  marked: 65536,
  bonded: 131072,
  frozen: 262144,
  held: 524288,
} as const;

function toInt(c: Codec, v: number): number {
  switch (c.k) {
    case "u": {
      const max = c.bits >= 32 ? 0xffffffff : (1 << c.bits) - 1;
      return Math.max(0, Math.min(max, Math.round(v)));
    }
    case "q": {
      const steps = (1 << c.bits) - 1;
      const t = v <= c.min ? 0 : v >= c.max ? 1 : (v - c.min) / (c.max - c.min);
      return Math.round(t * steps);
    }
    case "a": {
      const t = (((v / (Math.PI * 2)) % 1) + 1) % 1;
      return Math.round(t * ((1 << c.bits) - 1)) % (1 << c.bits);
    }
    case "f":
      return v;
  }
}

function fromInt(c: Codec, i: number): number {
  switch (c.k) {
    case "u":
      return i;
    case "q":
      return c.min + (i / ((1 << c.bits) - 1)) * (c.max - c.min);
    case "a": {
      let a = (i / ((1 << c.bits) - 1)) * Math.PI * 2;
      if (a > Math.PI) a -= Math.PI * 2;
      return a;
    }
    case "f":
      return i;
  }
}

const bitsOf = (c: Codec) => (c.k === "f" ? 32 : c.bits);

/** A replicated entity in quantized integer form. */
export interface EntityRecord {
  type: EntityType;
  ints: number[];
}

export type WorldRecords = Map<number, EntityRecord>;

export function encodeRecord(type: EntityType, fields: AnyFields): EntityRecord {
  return { type, ints: FIELD_LISTS[type].map(([name, codec]) => toInt(codec, fields[name] ?? 0)) };
}

export function decodeRecord(rec: EntityRecord): AnyFields {
  const out: AnyFields = {};
  const list = FIELD_LISTS[rec.type];
  for (let i = 0; i < list.length; i++) out[list[i]![0]] = fromInt(list[i]![1], rec.ints[i]!);
  return out;
}

// ---------------------------------------------------------------- local player (prediction) block

/** Exact (unquantized) predicted state of the receiving client's own player. */
export interface LocalState {
  netId: number;
  ackSeq: number;
  f32: Float32Array; // see LOCAL_F32 layout
  ints: Int32Array; // see LOCAL_INT layout
}

export const LOCAL_F32 = [
  "x", "y", "z", "vx", "vy", "vz", "ex", "ez",
  "slowPct", "ammo", "ult", "over",
  "hastePct", "stowed",
  "hv0", "hv1", "hv2", "hv3", "hv4", "hv5", "hv6", "hv7", "hv8", "hv9", "hv10", "hv11", "hv12", "hv13", "hv14", "hv15",
  "mSpeed", "mx", "my", "mz",
  "cd0", "cd1", "cd2", "cd3", "cd4", "cd5",
] as const;

export const LOCAL_INT = [
  "hero", "form", "baseHero", "disguiseTicks",
  "grounded", "crouch", "buttons", "aimSlot", "aimTicks", "aimMask",
  "stun", "root", "slow", "sleep", "invuln", "knockdown", "boost", "phased", "pinned", "antiHeal", "healBoost", "resist", "linked",
  "stealth", "haste", "unstoppable", "marked", "markedBy", "bonded", "bondedBy", "frozen", "held",
  "heldBy",
  "reload", "fireCd", "action", "actionTicks", "shots", "weaponSlot", "ultActive", "overTicks",
  "mKind", "mTicks", "mTarget", "mBlocked",
  "ch0", "ch1", "ch2", "ch3", "ch4", "ch5",
] as const;

export const LF = Object.fromEntries(LOCAL_F32.map((k, i) => [k, i])) as Record<(typeof LOCAL_F32)[number], number>;
export const LI = Object.fromEntries(LOCAL_INT.map((k, i) => [k, i])) as Record<(typeof LOCAL_INT)[number], number>;

// ---------------------------------------------------------------- events

const EV_TYPES = ["tracer", "impact", "explosion", "damage", "heal", "kill", "cast", "form", "pickup", "revive"] as const;

function writeVec(w: BitWriter, v: Vec3) {
  w.quant(v.x, -256, 256, 18).quant(v.y, -50, 50, 15).quant(v.z, -256, 256, 18);
}
function readVec(r: BitReader): Vec3 {
  return { x: r.quant(-256, 256, 18), y: r.quant(-50, 50, 15), z: r.quant(-256, 256, 18) };
}

export function writeEvent(w: BitWriter, e: SimEvent) {
  w.u16(e.id ?? 0);
  w.u32(e.tick ?? 0);
  w.bits(EV_TYPES.indexOf(e.type), 4);
  switch (e.type) {
    case "tracer":
      w.u16(e.owner);
      writeVec(w, e.from);
      writeVec(w, e.to);
      w.bits(e.kind, 9);
      break;
    case "impact":
      writeVec(w, e.pos);
      w.bits(e.kind, 9);
      break;
    case "explosion":
      writeVec(w, e.pos);
      w.quant(e.radius, 0, 16, 8);
      w.bits(e.kind, 9);
      break;
    case "damage":
      w.u16(e.attacker).u16(e.target).bits(Math.min(4095, e.amount), 12).bool(e.crit);
      writeVec(w, e.pos);
      break;
    case "heal":
      w.u16(e.healer).u16(e.target).bits(Math.min(4095, e.amount), 12);
      break;
    case "kill":
      w.u16(e.killer).u16(e.victim).u8(e.killerHero).u8(e.victimHero);
      for (const list of [e.kos, e.assists]) {
        const n = Math.min(7, list.length);
        w.bits(n, 3);
        for (let i = 0; i < n; i++) w.u16(list[i]!);
      }
      break;
    case "cast":
      w.u16(e.owner).bits(e.slot, 3).bits(e.hero, 6);
      break;
    case "form":
      w.u16(e.target).bits(e.hero, 6).bits(e.form, 2);
      break;
    case "pickup":
      writeVec(w, e.pos);
      w.bool(e.large);
      break;
    case "revive":
      w.u16(e.target);
      break;
  }
}

export function readEvent(r: BitReader): SimEvent {
  const id = r.u16();
  const tick = r.u32();
  const type = EV_TYPES[r.bits(4)];
  switch (type) {
    case "tracer":
      return { id, tick, type, owner: r.u16(), from: readVec(r), to: readVec(r), kind: r.bits(9) };
    case "impact":
      return { id, tick, type, pos: readVec(r), kind: r.bits(9) };
    case "explosion":
      return { id, tick, type, pos: readVec(r), radius: r.quant(0, 16, 8), kind: r.bits(9) };
    case "damage":
      return { id, tick, type, attacker: r.u16(), target: r.u16(), amount: r.bits(12), crit: r.bool(), pos: readVec(r) };
    case "heal":
      return { id, tick, type, healer: r.u16(), target: r.u16(), amount: r.bits(12) };
    case "kill": {
      const base = { killer: r.u16(), victim: r.u16(), killerHero: r.u8(), victimHero: r.u8() };
      const list = () => Array.from({ length: r.bits(3) }, () => r.u16());
      const kos = list();
      return { id, tick, type, ...base, kos, assists: list() };
    }
    case "cast":
      return { id, tick, type, owner: r.u16(), slot: r.bits(3), hero: r.bits(6) };
    case "form":
      return { id, tick, type, target: r.u16(), hero: r.bits(6), form: r.bits(2) };
    case "pickup":
      return { id, tick, type, pos: readVec(r), large: r.bool() };
    case "revive":
      return { id, tick, type, target: r.u16() };
    default:
      throw new Error("bad event type");
  }
}

// ---------------------------------------------------------------- snapshot packets

export const MSG_SNAPSHOT = 1;
export const MSG_INPUT = 2;

export interface SnapshotPacket {
  tick: number;
  baseline: number; // 0 = full snapshot
  /** Client time echo for RTT: last client timestamp received, and ms the server held it. */
  echoTime: number;
  echoHold: number;
  /** Server-side input queue depth for this client (drives client time dilation). */
  queue: number;
  events: SimEvent[];
  removed: number[];
  updates: { netId: number; rec: EntityRecord; isNew: boolean; mask?: number[] }[];
  local?: LocalState;
}

/** Writes a snapshot, delta-compressed against `base` (the client's acked world records). */
export function writeSnapshot(
  w: BitWriter,
  tick: number,
  baselineTick: number,
  base: WorldRecords | undefined,
  current: WorldRecords,
  events: SimEvent[],
  local: LocalState | undefined,
  echoTime: number,
  echoHold: number,
  queue: number,
) {
  w.u8(MSG_SNAPSHOT);
  w.u32(tick);
  w.u32(base ? baselineTick : 0);
  w.f32(echoTime);
  w.bits(Math.min(1023, Math.max(0, Math.round(echoHold))), 10);
  w.bits(Math.min(15, queue), 4);
  w.varuint(events.length);
  for (const e of events) writeEvent(w, e);

  const removed: number[] = [];
  if (base) for (const id of base.keys()) if (!current.has(id)) removed.push(id);
  w.varuint(removed.length);
  for (const id of removed) w.u16(id);

  const changed: [number, EntityRecord, EntityRecord | undefined][] = [];
  for (const [id, rec] of current) {
    const prev = base?.get(id);
    if (prev && prev.type === rec.type && prev.ints.every((v, i) => v === rec.ints[i])) continue;
    changed.push([id, rec, prev && prev.type === rec.type ? prev : undefined]);
  }
  w.varuint(changed.length);
  for (const [id, rec, prev] of changed) {
    w.u16(id);
    w.bool(!prev);
    const list = FIELD_LISTS[rec.type];
    if (!prev) {
      w.bits(rec.type, 3);
      for (let i = 0; i < list.length; i++) w.bits(rec.ints[i]!, bitsOf(list[i]![1]));
    } else {
      for (let i = 0; i < list.length; i++) w.bool(prev.ints[i] !== rec.ints[i]);
      for (let i = 0; i < list.length; i++) if (prev.ints[i] !== rec.ints[i]) w.bits(rec.ints[i]!, bitsOf(list[i]![1]));
    }
  }

  w.bool(!!local);
  if (local) {
    w.u16(local.netId);
    w.u16(local.ackSeq);
    for (const v of local.f32) w.f32(v);
    for (const v of local.ints) w.varuint(Math.max(0, v));
  }
}

/**
 * Reads a snapshot. `baseFor(tick)` returns the client's stored records for a baseline tick.
 * Returns the packet and the fully reconstructed world records for this tick.
 */
export function readSnapshot(r: BitReader, baseFor: (tick: number) => WorldRecords | undefined): { packet: SnapshotPacket; world: WorldRecords } | null {
  const tick = r.u32();
  const baseline = r.u32();
  const echoTime = r.f32();
  const echoHold = r.bits(10);
  const queue = r.bits(4);
  const events: SimEvent[] = [];
  const ne = r.varuint();
  for (let i = 0; i < ne; i++) events.push(readEvent(r));
  const base = baseline ? baseFor(baseline) : new Map<number, EntityRecord>();
  if (!base) return null; // baseline unknown — can't decode; server will resend from an older ack
  const world: WorldRecords = new Map(base);
  const removed: number[] = [];
  const nr = r.varuint();
  for (let i = 0; i < nr; i++) {
    const id = r.u16();
    removed.push(id);
    world.delete(id);
  }
  const updates: SnapshotPacket["updates"] = [];
  const nu = r.varuint();
  for (let i = 0; i < nu; i++) {
    const id = r.u16();
    const isNew = r.bool();
    if (isNew) {
      const type = r.bits(3) as EntityType;
      const list = FIELD_LISTS[type];
      const ints = list.map(([, c]) => r.bits(bitsOf(c)));
      const rec = { type, ints };
      world.set(id, rec);
      updates.push({ netId: id, rec, isNew });
    } else {
      const prev = world.get(id);
      if (!prev) return null;
      const list = FIELD_LISTS[prev.type];
      const mask = list.map(() => r.bool());
      const ints = prev.ints.slice();
      for (let k = 0; k < list.length; k++) if (mask[k]) ints[k] = r.bits(bitsOf(list[k]![1]));
      const rec = { type: prev.type, ints };
      world.set(id, rec);
      updates.push({ netId: id, rec, isNew });
    }
  }
  let local: LocalState | undefined;
  if (r.bool()) {
    const netId = r.u16();
    const ackSeq = r.u16();
    const f32 = new Float32Array(LOCAL_F32.length);
    for (let i = 0; i < f32.length; i++) f32[i] = r.f32();
    const ints = new Int32Array(LOCAL_INT.length);
    for (let i = 0; i < ints.length; i++) ints[i] = r.varuint();
    local = { netId, ackSeq, f32, ints };
  }
  return { packet: { tick, baseline, echoTime, echoHold, queue, events, removed, updates, local }, world };
}

// ---------------------------------------------------------------- input packets

export interface InputPacket {
  ackTick: number;
  clientTime: number;
  inputs: InputCmd[]; // newest first
}

export function writeInputPacket(w: BitWriter, p: InputPacket) {
  w.u8(MSG_INPUT);
  w.u32(p.ackTick);
  w.f32(p.clientTime);
  w.bits(p.inputs.length, 4);
  for (const i of p.inputs) writeInput(w, i);
}

/** Reads an input packet (after the message-type byte). */
export function readInputPacket(r: BitReader): InputPacket {
  const ackTick = r.u32();
  const clientTime = r.f32();
  const n = r.bits(4);
  const inputs: InputCmd[] = [];
  for (let i = 0; i < n; i++) inputs.push(readInput(r));
  return { ackTick, clientTime, inputs };
}
